import { Node } from '@tiptap/core';

export const S3Image = Node.create({
    name: 's3Image',
    group: 'block',
    draggable: true,
    selectable: true,
    atom: true,
    inline: false,
    allowGapCursor: true,

    addAttributes() {
        return {
            src: { default: null },
            key: { default: null },
            alt: { default: null },
            uploadId: { default: null },
            size: {
                default: 'full',
                parseHTML: element => element.getAttribute('data-size'),
            },
            textAlign: {
                default: 'left',
                parseHTML: element => element.getAttribute('data-text-align'),
            }
        };
    },

    parseHTML() {
        return [{
            tag: 'img[data-s3-key]',
            getAttrs: dom => ({
                src: dom.getAttribute('src'),
                key: dom.getAttribute('data-s3-key'),
                alt: dom.getAttribute('alt'),
                uploadId: dom.getAttribute('data-upload-id'),
                size: dom.getAttribute('data-size') || 'full',
                textAlign: dom.getAttribute('data-text-align') || 'left',
            }),
        }];
    },

    renderHTML({ HTMLAttributes }) {
        const sizes = {
            small: 'w-1/4',
            medium: 'w-1/2',
            large: 'w-3/4',
            full: 'w-full'
        };

        const alignments = {
            left: 'mr-auto',
            center: 'mx-auto',
            right: 'ml-auto'
        };

        const containerClasses = [
            's3-image-container',
            'node-s3Image',
            'relative',
            'block',
            'mt-4',
            'mb-1',
            'rounded-lg',
            'transition-colors',
            '[.ProseMirror-focused_&.ProseMirror-selectednode]:bg-neutral-50',
            '[.ProseMirror-focused_&.ProseMirror-selectednode]:dark:bg-neutral-900/50',
        ].join(' ');

        const imageClasses = [
            'rounded-lg',
            sizes[HTMLAttributes.size || 'full'],
            alignments[HTMLAttributes.textAlign || 'left'],
            'transition-all',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-2',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-violet-500',
            '[.ProseMirror-focused_.ProseMirror-selectednode_&]:outline-offset-4',
            HTMLAttributes.uploadId ? 'animate-pulse' : '',
        ].filter(Boolean).join(' ');

        return ['div', { 
            class: containerClasses,
            'data-type': 's3-image',
            draggable: 'true'
        }, [
            'img', {
                ...HTMLAttributes,
                'data-s3-key': HTMLAttributes.key,
                'data-upload-id': HTMLAttributes.uploadId,
                'data-size': HTMLAttributes.size,
                'data-text-align': HTMLAttributes.textAlign,
                class: imageClasses,
            }
        ]];
    },

    renderText({ node }) {
        return node.attrs.alt || '[Image]';
    },

    addCommands() {
        return {
            setS3Image: attributes => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: attributes
                });
            },
            updateS3Image: attributes => ({ commands }) => {
                return commands.updateAttributes(this.name, attributes);
            }
        };
    },
});

// Helper function to strip S3 image URLs before saving
export const stripS3ImageUrls = (content) => {
    if (!content || typeof content !== 'object') return content;

    if (content.type === 's3Image' && content.attrs) {
        const { src, ...restAttrs } = content.attrs;
        content.attrs = restAttrs;
    }

    if (content.content) {
        content.content = content.content.map(node => stripS3ImageUrls(node));
    }

    return content;
}; 